import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { Box, colors, List, ListItem, Stack, Typography } from '@mui/material';
import { Phone, AccessTimeFilled } from '@mui/icons-material';

import { useCallState } from '@frontend/features/calls/hooks/useCallState';
import { useTwilio } from '@frontend/features/communications/TwilioContextProvider/provider';
import { formatTimeDifference } from '@frontend/features/common/utils/helpers';

import { ContentStack, MessageContainer } from '../EmailItem/styled';
import { MessageProps } from '../../types';
import { Timeline } from '../Timeline';
import CallStatusIndicator from '../CallStatusIndicator';
import { useGetClientByContact } from '@frontend/features/clients/queries';

export const PhoneItem = forwardRef<HTMLDivElement, MessageProps>((props, ref) => {
  const { t } = useTranslation();
  const { callsSocketRef } = useTwilio();

  const { status, isLoading } = useCallState(callsSocketRef, {
    specificCallId: props.history.callId || '',
  });

  const phone = props.history.callTo?.replace('+', '');

  const { data: clientData } = useGetClientByContact({
    phone,
    agencyId: props.history.agencyId,
  });

  const authorName =
    `${props.history.authorFirstName || ''} ${props.history.authorLastName || ''}`.trim() || props.history.callFrom;

  const clientName = `${clientData?.firstName ?? ''} ${clientData?.lastName ?? ''}`.trim() || props.history.callTo;

  const createdAtTime = dayjs(props.history.createdAt).format('HH:mm');

  return (
    <MessageContainer ref={ref} sx={{ justifyContent: props.isCurrentUser ? 'flex-end' : 'flex-start' }}>
      <Stack
        order={props.isCurrentUser ? 1 : 2}
        mr={(theme) => theme.spacing(props.isCurrentUser ? 2.5 : 0)}
        ml={(theme) => theme.spacing(props.isCurrentUser ? 0 : 2.5)}
      >
        <Stack
          className="history-author"
          display="flex"
          flexDirection="row"
          justifyContent={props.isCurrentUser ? 'flex-end' : 'flex-start'}
          alignItems="center"
          pb={1}
          gap={2}
        >
          <Box gap={1} display="flex">
            <Box display="flex" gap={0.8} alignItems="center">
              <Typography variant="subtitle2">{authorName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('cases.view.content.phoneCallText')}
              </Typography>
              <Typography variant="subtitle2">{clientName}</Typography>
            </Box>
          </Box>
        </Stack>
        <Stack
          maxWidth={600}
          minWidth={400}
          borderRadius={2}
          border={(theme) => `1px solid ${theme.palette.divider}`}
          display="flex"
          flexDirection="column"
          justifyContent="flex-end"
        >
          <Box
            display="flex"
            padding={(theme) => theme.spacing(1, 2)}
            flexDirection="row"
            borderRadius="16px 16px 0 0"
            alignItems="center"
            gap={1}
            bgcolor={colors.indigo[400]}
          >
            <Phone sx={{ color: 'background.paper' }} />
            <Typography color="background.paper" variant="subtitle2">
              {t('cases.view.content.call')}
            </Typography>
          </Box>
          <Stack borderRadius="0 0 16px 16px" bgcolor={(theme) => theme.palette.background.paper} padding={2}>
            <ContentStack sx={{ backgroundColor: (theme) => theme.palette.grey[100], padding: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t(`cases.view.content.${props.history.callIsIncoming ? 'incomingCall' : 'outgoingCall'}`)}
              </Typography>
              <CallStatusIndicator
                status={status}
                fileId={props.history.callRecordingId}
                isLoading={isLoading}
                size="small"
              />
              {props.history.callTransfers && props.history.callTransfers.length > 0 && (
                <Box>
                  <List dense sx={{ display: 'grid', padding: 0 }}>
                    {props.history.callTransfers.map((transfer) => {
                      const tranferDuration = formatTimeDifference(
                        dayjs(transfer.createdAt).diff(dayjs(props.history.createdAt), 'second'),
                      );

                      return (
                        <ListItem key={transfer.id} sx={{ padding: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
                            <Typography variant="subtitle2" color="text.secondary">
                              {t('cases.view.content.callTransfers.transferredTo')}
                            </Typography>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle2">
                                {transfer.toAgentFirstName?.trim()} {transfer.toAgentLastName?.trim()}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <AccessTimeFilled sx={{ color: 'action.active' }} />
                              <Typography paddingTop={0.3} variant="body2" color="text.secondary">
                                {tranferDuration}
                              </Typography>
                            </Box>
                          </Box>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              )}
            </ContentStack>
          </Stack>
        </Stack>
        <Typography textAlign="right" pt={1} color="text.disabled" variant="subtitle2">
          {createdAtTime}
        </Typography>
      </Stack>

      <Timeline
        isCurrentUser={props.isCurrentUser}
        icon={Phone}
        color="background.paper"
        background={colors.indigo[400]}
      />
    </MessageContainer>
  );
});

PhoneItem.displayName = 'PhoneItem';
